import { useEffect } from 'react';

/**
 * Lightweight client-side SEO meta manager (no extra library needed).
 * Updates <title>, <meta name="description">, canonical link, Open Graph
 * + Twitter cards, and an optional JSON-LD <script> block.
 *
 * Cleans up on unmount so navigating back doesn't leave stale tags.
 */
export const useDocumentMeta = ({
  title,
  description,
  canonical,
  image,
  type = 'article',
  jsonLd,
  keywords,
} = {}) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => k !== 'content' && el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute('content', attrs.content || '');
      return { el, created };
    };

    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      return { el, created };
    };

    const created = [];
    if (description) {
      const r = upsertMeta('meta[name="description"]', { name: 'description', content: description });
      if (r.created) created.push(r.el);
    }
    if (keywords) {
      const r = upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
      if (r.created) created.push(r.el);
    }
    if (canonical) {
      const r = upsertLink('canonical', canonical);
      if (r.created) created.push(r.el);
    }

    // Open Graph
    const og = [
      ['og:type',        type],
      ['og:title',       title || ''],
      ['og:description', description || ''],
      ['og:url',         canonical || (typeof window !== 'undefined' ? window.location.href : '')],
      ['og:image',       image || ''],
      ['og:locale',      'hu_HU'],
      ['og:site_name',   'Orosz Coaching'],
    ];
    og.forEach(([property, content]) => {
      if (!content) return;
      const r = upsertMeta(`meta[property="${property}"]`, { property, content });
      if (r.created) created.push(r.el);
    });

    // Twitter
    const tw = [
      ['twitter:card',        image ? 'summary_large_image' : 'summary'],
      ['twitter:title',       title || ''],
      ['twitter:description', description || ''],
      ['twitter:image',       image || ''],
    ];
    tw.forEach(([name, content]) => {
      if (!content) return;
      const r = upsertMeta(`meta[name="${name}"]`, { name, content });
      if (r.created) created.push(r.el);
    });

    // JSON-LD structured data
    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement('script');
      ldEl.type = 'application/ld+json';
      ldEl.text = JSON.stringify(jsonLd);
      ldEl.setAttribute('data-orosz-jsonld', '1');
      document.head.appendChild(ldEl);
    }

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
    };
  }, [title, description, canonical, image, type, keywords, JSON.stringify(jsonLd)]);
};
